import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Modal } from './Modal';

/**
 * The app's own confirm and prompt, in place of the browser's.
 *
 * `window.confirm` says "localhost:5180 says" above whatever you wrote, cannot be styled,
 * blocks the whole tab, and looks like something has gone wrong rather than like the
 * application asking a question. This is the same thing in the app's own dialog.
 *
 * Deliberately promise-based so it drops into the code the native ones occupied without
 * turning every call site inside out:
 *
 *     if (!(await ask.confirm({ title: 'Delete it?' }))) return;
 *     const why = await ask.prompt({ title: 'Why?' });
 *
 * `confirm` resolves false and `prompt` resolves null when dismissed, exactly as the browser's
 * did — so cancelling still means "do nothing" everywhere, including on Escape or a click on
 * the backdrop.
 */
interface ConfirmRequest {
  title: string;
  /** Blank line separated; each paragraph gets its own line, as the native ones did. */
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button, for anything that removes or lowers something. */
  danger?: boolean;
}

interface PromptRequest extends ConfirmRequest {
  label?: string;
  placeholder?: string;
  initial?: string;
  /** Keeps the confirm button disabled until this much has been typed. */
  minLength?: number;
  multiline?: boolean;
}

/**
 * More than two outcomes. Exists because a native confirm forces a three-way decision into
 * OK/Cancel, and then dismissing the dialog silently performs whichever one Cancel meant —
 * see the sprint completion, where Escape used to move unfinished work to the backlog.
 * Here dismissal is its own answer: null, meaning do nothing.
 */
interface ChooseRequest {
  title: string;
  body?: string;
  options: Array<{ value: string; label: string; danger?: boolean; quiet?: boolean }>;
}

interface Ask {
  confirm: (request: ConfirmRequest) => Promise<boolean>;
  prompt: (request: PromptRequest) => Promise<string | null>;
  choose: (request: ChooseRequest) => Promise<string | null>;
}

const AskContext = createContext<Ask | null>(null);

type Pending =
  | { kind: 'confirm'; request: ConfirmRequest; resolve: (answer: boolean) => void }
  | { kind: 'prompt'; request: PromptRequest; resolve: (answer: string | null) => void }
  | { kind: 'choose'; request: ChooseRequest; resolve: (answer: string | null) => void };

export function AskProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [value, setValue] = useState('');

  /**
   * Held in a ref as well as state so `close` can settle the promise without depending on
   * the render that is being torn down — a dialog that resolves nothing leaves the caller
   * awaiting forever, which looks like the button having done nothing at all.
   */
  const live = useRef<Pending | null>(null);

  const settle = useCallback((answer: boolean | string | null) => {
    const current = live.current;
    live.current = null;
    setPending(null);
    setValue('');
    if (!current) return;

    if (current.kind === 'confirm') current.resolve(answer === true);
    else current.resolve(typeof answer === 'string' ? answer : null);
  }, []);

  const confirm = useCallback((request: ConfirmRequest) => {
    return new Promise<boolean>((resolve) => {
      const next: Pending = { kind: 'confirm', request, resolve };
      live.current = next;
      setPending(next);
    });
  }, []);

  const prompt = useCallback((request: PromptRequest) => {
    return new Promise<string | null>((resolve) => {
      const next: Pending = { kind: 'prompt', request, resolve };
      live.current = next;
      setValue(request.initial ?? '');
      setPending(next);
    });
  }, []);

  const choose = useCallback((request: ChooseRequest) => {
    return new Promise<string | null>((resolve) => {
      const next: Pending = { kind: 'choose', request, resolve };
      live.current = next;
      setPending(next);
    });
  }, []);

  const api = useMemo<Ask>(() => ({ confirm, prompt, choose }), [confirm, prompt, choose]);

  const request = pending?.request;
  const isPrompt = pending?.kind === 'prompt';
  const chooseRequest = pending?.kind === 'choose' ? (pending.request as ChooseRequest) : null;
  const promptRequest = isPrompt ? (pending.request as PromptRequest) : null;
  const tooShort = !!promptRequest && value.trim().length < (promptRequest.minLength ?? 1);

  return (
    <AskContext.Provider value={api}>
      {children}

      {pending && request && (
        <Modal title={request.title} onClose={() => settle(isPrompt ? null : false)}>
          {'body' in request && request.body && (
            <div className="stack-sm">
              {request.body
                .split(/\n\n+/)
                .map((paragraph) => paragraph.trim())
                .filter(Boolean)
                .map((paragraph) => (
                  <p key={paragraph} style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>
                    {paragraph}
                  </p>
                ))}
            </div>
          )}

          {promptRequest && (
            <label className="field" style={{ marginTop: request.body ? 14 : 0, marginBottom: 0 }}>
              {promptRequest.label && <span className="field-label">{promptRequest.label}</span>}
              {promptRequest.multiline ? (
                <textarea
                  value={value}
                  autoFocus
                  rows={4}
                  placeholder={promptRequest.placeholder}
                  onChange={(event) => setValue(event.target.value)}
                />
              ) : (
                <input
                  value={value}
                  autoFocus
                  placeholder={promptRequest.placeholder}
                  onChange={(event) => setValue(event.target.value)}
                  // Enter accepts, the way it would in the browser's own prompt.
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !tooShort) settle(value);
                  }}
                />
              )}
            </label>
          )}

          {chooseRequest ? (
            <div className="row wrap" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
              {/* Dismissing is not one of these, and that is the point: it does nothing. */}
              <button type="button" className="btn btn-quiet" onClick={() => settle(null)}>
                Cancel
              </button>
              {chooseRequest.options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={option.danger ? 'btn btn-danger' : option.quiet ? 'btn' : 'btn btn-primary'}
                  onClick={() => settle(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="row" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
              <button type="button" className="btn btn-quiet" onClick={() => settle(isPrompt ? null : false)}>
                {(request as ConfirmRequest).cancelLabel ?? 'Cancel'}
              </button>
              <button
                type="button"
                className={(request as ConfirmRequest).danger ? 'btn btn-danger' : 'btn btn-primary'}
                autoFocus={!isPrompt}
                disabled={isPrompt && tooShort}
                onClick={() => settle(isPrompt ? value : true)}
              >
                {(request as ConfirmRequest).confirmLabel ?? (isPrompt ? 'Save' : 'Yes, go ahead')}
              </button>
            </div>
          )}
        </Modal>
      )}
    </AskContext.Provider>
  );
}

export function useAsk(): Ask {
  const ctx = useContext(AskContext);
  if (!ctx) throw new Error('useAsk must be used inside an AskProvider');
  return ctx;
}
